import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {TreeComponent} from '../../../components/v2/tree/tree.component';
import {ActivatedRoute, Router} from '@angular/router';
import {concat, defer, EMPTY, merge, Observable, Subject, Subscription} from 'rxjs';
import {CustomerService} from '@service/crm/customer.service';
import {MatDialog} from '@angular/material/dialog';
import {LeadDetailComponent} from '../lead-detail/lead-detail.component';
import {CustomerDetailCrmComponent} from '../customer-detail-crm/customer-detail-crm.component';
import {CrmSection} from '@enum/crm-section';
import {AuthService} from '@service/auth/auth.service';
import {DashboardCrmService} from '@service/dashboard-crm/dashboard-crm.service';
import {ModalService} from '@service/modal/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {filter, map, startWith, switchMap, tap, toArray} from 'rxjs/operators';
import {getMessageAction, MessagesToRead, updateResponseForMessages} from '../../../shared/utils/messages.utils';
import {
  DataSourceObservableFn,
  DataSourceProvider,
  getDataSourceObservable
} from '../../../shared/utils/data.source.provider';
import {Pagination} from '@interface/pagination';
import {CommonService} from '@service/common/common.service';
import {LeadService} from '@service/crm/lead.service';
import {getMapSelection} from '../../../shared/utils/functions.utils';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MultiSelectObject} from '@interface/multi-select-object';


const DEFAULT_EVALUATION_LEAD = '|Prospect|Lover|Press|Supplier';

@Component({
    selector: 'app-customers',
    templateUrl: './customers.component.html',
    styleUrls: ['customers.component.scss'],
    animations: [
        trigger('slideToggle', [
            state('hidden', style({
                height: '0px',
                opacity: 0,
                overflow: 'hidden'
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('hidden <=> visible', [
                animate('300ms ease-in-out')
            ])
        ])
    ],
    standalone: false
})
export class CustomersComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(TreeComponent) tree: TreeComponent | undefined;
  @ViewChild('messageRowButton', {static: true}) messageRowButton!: TemplateRef<any>;
  private readonly localStorageFilterExpKey = 'filterMenuExpanded';
  public searchForm?: UntypedFormGroup;
  public CrmSection = CrmSection;
  public leads: any = undefined;
  public verifiedContacts: any = undefined;
  public customers: any = undefined;
  public countrySelection: any;
  public allCountrySelection?: { name: string; value: number }[];
  public allOriginSelection?: { name: string; value: number }[];
  public originSelection: any;
  public jobTitleSelection!: MultiSelectObject[];
  public evaluationSelectionLeads: any;
  public evaluationSelection: any;
  public assignedToSelection: any;
  public allAssignedToSelection?: { name: string; value: number }[];
  public messagesToReadSelection: { name: string; value: string; disabled?: boolean | undefined }[] = [];
  public customerChannelSelection?: { name: string; value: number }[];
  public allSubs: Subscription[] = [];
  public matTabIndex = 0;
  public hideColumns = ['id'];
  public actions?: { [key: string]: ({
      icon: string,
      func: (id: number, row?: any) => void
    } | {
      templateOutlet: TemplateRef<any>,
      context?: (row: any) => any,
    })[] };
  public widgetOptions = {
    rating: 'star',
    createDate: 'dateTime'
  };
  public crmSection: CrmSection;
  public sections: CrmSection[];
  public showUndo!: boolean;
  private crmSectionSubj = new Subject<CrmSection>();
  private searchSubject = new Subject();
  public dataSourceCustomers: DataSourceObservableFn;
  public dataSourceVerifiedContacts: DataSourceObservableFn;
  public CustomerColumns = ['code', 'name', 'email', 'origin', 'country', 'assignedTo', 'jobTitle', 'customerChannel'];
  public VerifiedContactsColumns = ['name', 'email', 'origin', 'country', 'evaluation', 'rating', 'assignedTo', 'jobTitle', 'customerChannel'];
  private updateSubject = new Subject();
  filterExpanded = true;
  canLoadAnimation = false;

  constructor(
    private fb: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private customersApi: CustomerService,
    private commonService: CommonService,
    private leadService: LeadService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private translate: TranslateService,
    private dashboardService: DashboardCrmService,
    private authService: AuthService,
    private modalService: ModalService,
    private dialog: MatDialog
  ) {
    this.dashboardService.title = 'CRM - Customers';
    this.sections = [CrmSection.Lead, CrmSection.VerifiedContact, CrmSection.CustomerList].filter((s) => this.canSeeTab(s));
    this.crmSection = this.sections?.[0] || 0;
    this.filterExpanded = localStorage.getItem(this.localStorageFilterExpKey) !== '0';
    this.activateTabViaPathParam();
    this.subscribeCrmSectionUpdate();
    this.setupMessageToReadSelection();
    this.dataSourceCustomers = getDataSourceObservable(
      this.getCustomerObservable(
        (params) => this.customersApi.getCustomers(params),
        CrmSection.CustomerList
      )
    );
    this.dataSourceVerifiedContacts = getDataSourceObservable(
      this.getCustomerObservable(
        (params) => this.customersApi.getVerifiedContacts(params),
        CrmSection.VerifiedContact
      )
    );
  }

  private setSearchFormForTab(section: CrmSection): void {
    this.searchForm = this.fb.group({
      createDate: this.fb.group({
        type: ['date'],
        from: [],
        to: []
      }),
      code: [],
      name: [],
      email: [],
      country: [],
      origin: [],
      jobTitle: [],
      evaluation: section === CrmSection.Lead ? this.fb.group({
        type: ['strict'],
        value: [DEFAULT_EVALUATION_LEAD]
      }) : [],
      assignedTo: [],
      messagesToRead: [],
      taskStatus: [],
      customerChannel: []
    });
  }

  ngAfterViewInit(): void {
    this.actions = {
      lead: [
        getMessageAction((id: number) => this.openLeadWithId(id, true), this.messageRowButton),
        // { Replaced with rowClick
        //   icon: 'fa-eye',
        //   func: (id: number) => this.openLeadWithId(id)
        // }
      ],
      verifiedContacts: [
        getMessageAction((id: number) => this.openCustomerWithId(id, true), this.messageRowButton),
        // { Replaced with rowClick
        //   icon: 'fa-eye',
        //   func: (id: number) => this.openCustomerWithId(id)
        // }
      ],
      customers: [
        getMessageAction((id: number) => this.openCustomerWithId(id, true), this.messageRowButton),
        // getTaskAction((id: number) => this.openLeadWithId(id, true), this.taskRowButton),
        // { Replaced with rowClick
        //   icon: 'fa-eye',
        //   func: (id: number) => this.openCustomerWithId(id)
        // }
      ],
    };
    this.cd.detectChanges();
    if (this.crmSection === CrmSection.Lead) {
      this.doSearch();
    }
  }

  private activateTabViaPathParam(): void {
    this.allSubs.push(this.activatedRoute.data.subscribe((data) => {
      this.crmSection = data.section;
      this.matTabIndex = this.sections.indexOf(this.crmSection);
      this.setSearchFormForTab(this.crmSection);
    }));
  }

  private openLeadWithId(id: number, communicationTab?: boolean, taskTab?: boolean): void {
    const template = this.dialog.open(LeadDetailComponent, {
      width: '90%',
      height: '90%',
      panelClass: 'p-0',
      disableClose: true
    });
    template.componentInstance.leadId = id;
    if (communicationTab && !taskTab) {
      template.componentInstance.selectCommunicationTab();
    }
    // if (!communicationTab && taskTab) {
    //   template.componentInstance.selectCommunicationTab();
    // }
    this.allSubs.push(template.afterClosed().subscribe((data) => {
      if (data.modified) {
        this.updateData();
      }
      if (data.customerId) {
        this.goToSection(CrmSection.VerifiedContact);
        this.openCustomerWithId(data.customerId);
      }
    }));
  }

  private openCustomerWithId(id: number, communicationTab?: boolean): void {
    const template = this.dialog.open(CustomerDetailCrmComponent, {
      width: '90%',
      height: '90%',
      panelClass: 'p-0',
      disableClose: true
    });
    template.componentInstance.customerId = id;
    if (communicationTab) {
      template.componentInstance.selectCommunicationTab();
    }
    this.allSubs.push(template.afterClosed().subscribe((data) => {
      if (data.modified) {
        this.updateData();
      }
    }));
  }

  openRowDetail(row: any): void {
    if (this.crmSection === CrmSection.Lead) {
      this.openLeadWithId(row.id);
    } else {
      this.openCustomerWithId(row.id);
    }
  }

  ngOnInit(): void {
    this.updateData();
  }

  ngOnDestroy(): void {
    this.allSubs.forEach((x) => x.unsubscribe());
  }

  doSearch(): void {
    if ([CrmSection.CustomerList, CrmSection.VerifiedContact].includes(this.crmSection)) {
      this.searchSubject.next(null);
    } else {
      const data = this.searchForm?.getRawValue();
      this.tree?.applyFilter(data);
    }
  }

  clearFilter(): void {
    this.setSearchFormForTab(this.crmSection);
    if (this.crmSection === CrmSection.Lead) {
      this.tree?.clearFilter();
      this.showUndo = true;
    } else {
      this.doSearch();
    }
  }

  clearFiltersAndDoSearch(): void {
    this.clearFilter();
    this.doSearch();
    this.showUndo = false;
  }

  prepareSelection(key: string, from: any): { name: string; value: string; disabled?: boolean | undefined; }[] {
    return [...new Set([''].concat(from?.map((x: any) => x[key])))].sort().map((z) => ({
      name: z,
      value: z
    }));
  }

  updateUrl(index: number): void {
    this.goToSection(this.sections[index]);
  }

  private goToSection(section: CrmSection): void {
    const links: { [key in CrmSection]?: string } = {
      [CrmSection.Lead]: 'lead',
      [CrmSection.VerifiedContact]: 'verified-contacts',
      [CrmSection.CustomerList]: 'contact-list',
    };
    if (section in links) {
      this.router.navigateByUrl('/crm/customers/' + links[section]);
    }
  }

  updateData(): void {
    this.crmSectionSubj.next(this.crmSection);
  }

  subscribeCrmSectionUpdate(): void {
    this.allSubs.push(
      this.crmSectionSubj.pipe(
        switchMap((crmSection) => {
          switch (crmSection) {
            default:
            case CrmSection.Lead:
              return this.customersApi.getLeads().pipe(tap((leads) => {
                this.leads = updateResponseForMessages(leads);
                this.prepareAllFilters(this.leads);
              }));
            case CrmSection.VerifiedContact:
            case CrmSection.CustomerList:
              return EMPTY;
          }
        })
      ).subscribe()
    );
  }

  prepareAllFilters(from: any): void {
    this.countrySelection = this.prepareSelection('country', from);
    this.originSelection = this.prepareSelection('origin', from);
    this.evaluationSelectionLeads = this.prepareSelection('evaluation', from)
      .map(({name, value}) => ({
        name: name || '',
        value: value || DEFAULT_EVALUATION_LEAD
      }));
    this.assignedToSelection = this.prepareSelection('assignedTo', from);
    this.cd.detectChanges();
  }

  private canSeeTab(tab: CrmSection): boolean {
    return this.authService.currentUserPermission.crmSectionsVisible.includes(tab);
  }

  trackByIndex(index: number): number {
    return index;
  }

  createCustomer(): void {
    this.allSubs.push(this.modalService
      .openRegisterModal()
      .afterClosed()
      .subscribe((created) => {
        if (created) {
          this.updateData();
        }
      }));
  }

  private getCustomerObservable(
    fn: (params: any) => Observable<any>,
    section: CrmSection
  ): DataSourceProvider<any> {
    return {
      getBeData: (params: any, pagination: Pagination, orderBy?: string) =>
        merge(fn({...params, pagination, orderBy}), this.prepareBeFilters())
          .pipe(
            map((customers) => ({
              count: customers.count,
              list: (updateResponseForMessages(customers.customers) as any)
            }))
          ),
      /*mapSortingFieldsBe: { // todo
        country: 'country_id'
      },*/
      searchObservable: this.searchSubject.pipe(
        startWith(null),
        filter(() => this.crmSection === section),
        map(() => this.searchForm?.getRawValue())
      ),
      updateData: this.updateSubject.pipe(
        filter(() => this.crmSection === section),
      )
    };
  }

  private prepareBeFilters(): Observable<void> {
    return merge(
      this.prepareAllCountrySelection(),
      this.prepareAllOriginSelection(),
      this.prepareAllReferrerSelection(),
      this.prepareCustomerChannelSelection(),
      this.prepareMainJobList(),
      this.prepareEvaluationSelection()
    ).pipe(filter(() => false));
  }

  private prepareAllCountrySelection(): Observable<any> {
    return defer(() => {
      if (this.allCountrySelection) {
        return EMPTY;
      } else {
        return this.commonService.countryList().pipe(
          getMapSelection('id' as const, 'description'),
          tap((countryList) => {
            this.allCountrySelection = countryList;
          })
        );
      }
    });
  }

  private prepareMainJobList(): Observable<any> {
    return defer(() => {
      if (this.jobTitleSelection) {
        return EMPTY;
      } else {
        return this.commonService.getJobTitles().pipe(
          tap((list) => {
            this.jobTitleSelection = list;
          }
          )
        );
      }
    });
  }

  private prepareEvaluationSelection(): Observable<any> {
    return defer(() => {
      if (this.evaluationSelection) {
        return EMPTY;
      } else {
        return this.leadService.getContactClassifications('customer').pipe(
          getMapSelection('id' as const, 'description'),
          tap((list) => {
            this.evaluationSelection = list;
          })
        );
      }
    });
  }

  private setupMessageToReadSelection(): void {
    this.allSubs.push(concat(...[MessagesToRead.NotToRead, MessagesToRead.ToRead]
      .map((value) =>
        this.translate.get(value).pipe(
          map((translation) => ({
            name: translation,
            value
          }))
        )
      )).pipe(toArray()).subscribe((messageSelections) => {
      this.messagesToReadSelection = messageSelections;
    }));
  }

  private prepareAllOriginSelection(): Observable<any> {
    return defer(() => {
      if (this.allOriginSelection) {
        return EMPTY;
      } else {
        return this.commonService.getOriginList().pipe(
          map(({origins}) => origins),
          getMapSelection('id' as const, 'name'),
          tap((originList) => {
            this.allOriginSelection = originList;
          })
        );
      }
    });
  }

  private prepareAllReferrerSelection(): Observable<any> {
    return defer(() => {
      if (this.allAssignedToSelection) {
        return EMPTY;
      } else {
        return this.commonService.getReferrerList().pipe(
          map(({referrers}) => referrers),
          getMapSelection('id' as const, 'name'),
          tap((referrerList) => {
            this.allAssignedToSelection = referrerList;
          })
        );
      }
    });
  }

  private prepareCustomerChannelSelection(): Observable<any> {
    return defer(() => {
      if (this.customerChannelSelection) {
        return EMPTY;
      } else {
        return this.commonService.channelList().pipe(
          getMapSelection('id' as const, 'description'),
          tap((channelList) => {
            this.customerChannelSelection = channelList;
          })
        );
      }
    });
  }

  toggleFilters(): void {
    this.canLoadAnimation = true;
    this.filterExpanded = !this.filterExpanded;
    localStorage.setItem(this.localStorageFilterExpKey, String(+this.filterExpanded));
    }
}

